import React, { useLayoutEffect, useMemo, useRef } from "react";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
} from "@mui/material";
import { darken, type SxProps, type Theme } from "@mui/material/styles";
import {
  getFlatPortfolioColors,
  portfolioTypography,
} from "../../../utils/getFlatPortfolioColors";
import { usePagination } from "../hooks/usePagination";
import { useSorting, SortConfig, SortDirection } from "../hooks/useSorting";
import SortableColumnHeader from "./SortableColumnHeader";
import PaginationControls from "./PaginationControls";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  width?: string | number;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  // Optional custom sort-value resolver. Use when the column's raw value
  // doesn't yield the desired order (e.g. an enum column that should sort
  // by priority rather than alphabetically). Return undefined to fall back
  // to the default nested-path extraction.
  sortAccessor?: (row: T) => unknown;
}

// Rows pinned above the body in a tbody of their own, kept in the caller's
// order (a sort applies to the body only). Section rows span the table and,
// with a pinned first column, hold to its visible width.
export interface DataTablePinned<T> {
  rows: T[];
  header: React.ReactNode;
  // After the pinned rows (e.g. "Show more").
  footer?: React.ReactNode;
  // Before the body, while it has rows.
  bodyHeader?: React.ReactNode;
  rowSx?: SxProps<Theme>;
  // Data attributes for a pinned row, which `rowSx` can select on.
  rowData?: (row: T) => Record<`data-${string}`, string | undefined>;
  // The id of the element in `header` that names the pinned rows.
  labelledBy?: string;
  // While the body has no rows, in its place: skeleton rows as it loads,
  // after `bodyHeader`, each cell's content from `cell`...
  loadingRows?: {
    count: number;
    cell: (columnIndex: number) => React.ReactNode;
  };
  // ...or a panel spanning the table (an error, no matches).
  panel?: React.ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  // Pagination
  pagination?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  // Sorting
  sortable?: boolean;
  defaultSort?: SortConfig<T>;
  // Styling
  stickyHeader?: boolean;
  maxHeight?: string | number;
  // Loading/Empty states
  loading?: boolean;
  emptyMessage?: string;
  // Mobile
  mobileCardRender?: (row: T, index: number) => React.ReactNode;
  // Row key
  getRowKey?: (row: T, index: number) => string | number;
  // Server mode: the caller owns sorting and paging (rows arrive already
  // ordered, pages append outside). `loading` never replaces the rows with a
  // spinner; the caller dims or skeletons instead.
  serverMode?: boolean;
  // One sorted column, or several shown sorted at once (the screener's ranking).
  sort?:
    | { key: string; direction: SortDirection }
    | { key: string; direction: SortDirection }[]
    | null;
  onSortChange?: (key: string) => void;
  // Expandable rows: one row open at a time, rendered beneath it across every column.
  renderExpanded?: (row: T, index: number) => React.ReactNode;
  animateExpanded?: boolean;
  expandedRowKey?: string | number | null;
  onRowClick?: (row: T, index: number, rowKey: string | number) => void;
  onRowHover?: (row: T) => void;
  onRowLeave?: () => void;
  // Pins the first column while the table scrolls sideways inside its own
  // container; the page never scrolls horizontally.
  stickyFirstColumn?: boolean;
  // Rendered inside the scroll container after the rows (a paging sentinel,
  // "Loading more"), held to the visible width when the table scrolls sideways.
  footer?: React.ReactNode;
  pinned?: DataTablePinned<T>;
  // Skeleton rows after every other row (the next page loading), each
  // cell's content from `cell`.
  loadingRows?: {
    count: number;
    cell: (columnIndex: number) => React.ReactNode;
  };
}

// Helper to get nested value from object
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const DataTable = <T extends object>({
  data,
  columns,
  pagination = true,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  sortable = true,
  defaultSort,
  stickyHeader = false,
  maxHeight,
  loading = false,
  emptyMessage = "No data available",
  mobileCardRender,
  getRowKey,
  serverMode = false,
  sort: controlledSort,
  onSortChange,
  renderExpanded,
  expandedRowKey = null,
  animateExpanded = false,
  onRowClick,
  onRowHover,
  onRowLeave,
  stickyFirstColumn = false,
  footer,
  pinned,
  loadingRows,
}: DataTableProps<T>) => {
  const theme = useTheme();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const colors = getFlatPortfolioColors(theme);

  // Sorting — pass a per-column resolver so columns with custom orderings
  // (sortAccessor) can override the default value extraction.
  const sortValueResolver = useMemo(
    () => (row: T, key: string) => {
      const col = columns.find((c) => String(c.key) === key);
      return col?.sortAccessor ? col.sortAccessor(row) : undefined;
    },
    [columns],
  );
  const sorting = useSorting(data, defaultSort, sortValueResolver);
  const sortedData = serverMode ? data : sorting.sortedData;
  const requestSort = serverMode
    ? (onSortChange ?? (() => undefined))
    : sorting.requestSort;
  const controlledSorts = controlledSort ? [controlledSort].flat() : [];
  const getSortDirection = (key: keyof T | string): SortDirection | null =>
    serverMode
      ? (controlledSorts.find((entry) => entry.key === String(key))
          ?.direction ?? null)
      : sorting.getSortDirection(key);
  const paginate = pagination && !serverMode;

  // A pinned expanded row spans the table's visible width. 100cqw counts a
  // classic vertical scrollbar as width, which let the table scroll sideways
  // by the scrollbar's few pixels, so the measured client width is used.
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const pinnedRows = pinned?.rows ?? [];
  const spansVisibleWidth =
    renderExpanded !== undefined || pinned !== undefined;
  const pinsExpanded = stickyFirstColumn && spansVisibleWidth;
  useLayoutEffect(() => {
    const box = scrollBoxRef.current;
    if (!box || !pinsExpanded) return undefined;
    const update = () =>
      box.style.setProperty("--visible-width", `${box.clientWidth}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    return () => observer.disconnect();
  }, [pinsExpanded]);

  // Pagination
  const paginationResult = usePagination(sortedData, {
    defaultPageSize,
    defaultPage: 1,
  });

  const {
    currentPageData,
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    setPageSize,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    startIndex,
    endIndex,
  } = paginationResult;

  // Display data (paginated or all)
  const displayData = paginate ? currentPageData : sortedData;

  // Generate row key
  const generateRowKey = (row: T, index: number): string | number => {
    if (getRowKey) {
      return getRowKey(row, index);
    }
    // Try common key fields
    const possibleKeys = ["id", "key", "symbol", "ticker", "date"];
    for (const key of possibleKeys) {
      const value = (row as any)[key];
      if (value !== undefined && value !== null) {
        return `${value}-${index}`;
      }
    }
    return index;
  };

  // Loading state (server mode keeps the rows on screen; the caller dims them)
  if (loading && !serverMode) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress size={32} sx={{ color: colors.functional.success }} />
      </Box>
    );
  }

  // Empty state
  if (data.length === 0 && pinnedRows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
          backgroundColor: colors.background.primary,
        }}
      >
        <Typography
          sx={{
            ...portfolioTypography.body,
            color: colors.text.secondary,
          }}
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile && mobileCardRender) {
    return (
      <Box sx={{ backgroundColor: colors.background.primary }}>
        {/* Cards */}
        <Stack spacing={1.5} sx={{ px: 2, py: 1 }}>
          {displayData.map((row, index) => (
            <Card
              key={generateRowKey(row, index)}
              sx={{
                backgroundColor: colors.background.secondary,
                border: `1px solid ${colors.border.primary}`,
                borderRadius: 0,
              }}
            >
              {mobileCardRender(row, startIndex + index)}
            </Card>
          ))}
        </Stack>

        {/* Pagination for mobile */}
        {paginate && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            startIndex={startIndex}
            endIndex={endIndex}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={goToNextPage}
            onPreviousPage={goToPreviousPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            pageSizeOptions={pageSizeOptions}
          />
        )}
      </Box>
    );
  }

  // Desktop table view
  const rowBackgroundColor = darken(theme.palette.background.surface, 0.05);

  // A row across every column, held to the visible width like an expansion.
  const sectionRow = (content: React.ReactNode) => (
    <TableRow sx={{ backgroundColor: rowBackgroundColor }}>
      <TableCell
        colSpan={columns.length}
        sx={{
          p: 0,
          borderTop: `1px solid ${colors.border.secondary}`,
          borderBottom: `1px solid ${colors.border.secondary}`,
        }}
      >
        <Box
          sx={
            stickyFirstColumn
              ? {
                  position: "sticky",
                  left: 0,
                  width: "var(--visible-width, 100cqw)",
                }
              : undefined
          }
        >
          {content}
        </Box>
      </TableCell>
    </TableRow>
  );

  const cellSx = (
    column: Column<T>,
    columnIndex: number,
    expanded: boolean,
  ) => ({
    py: 1.5,
    px: 2,
    // An open row runs straight into its expansion.
    borderBottom: expanded ? "none" : `1px solid ${colors.border.secondary}`,
    width: column.width,
    ...(stickyFirstColumn && columnIndex === 0
      ? {
          position: "sticky" as const,
          left: 0,
          zIndex: 1,
          backgroundColor: rowBackgroundColor,
        }
      : {}),
  });

  const skeletonRows = (rows: NonNullable<DataTableProps<T>["loadingRows"]>) =>
    Array.from({ length: rows.count }, (_, index) => (
      <TableRow
        key={index}
        aria-hidden
        sx={{ backgroundColor: rowBackgroundColor }}
      >
        {columns.map((column, columnIndex) => (
          <TableCell
            key={String(column.key)}
            align={column.align ?? "left"}
            sx={cellSx(column, columnIndex, false)}
          >
            {rows.cell(columnIndex)}
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderRow = (
    row: T,
    rowIndex: number,
    globalIndex: number,
    rowSx?: SxProps<Theme>,
    rowData?: Record<`data-${string}`, string | undefined>,
  ) => {
    const rowKey = generateRowKey(row, rowIndex);
    const expanded =
      renderExpanded !== undefined &&
      expandedRowKey !== null &&
      expandedRowKey === rowKey;
    const clickable = onRowClick !== undefined;
    return (
      <React.Fragment key={rowKey}>
        <TableRow
          onClick={
            clickable
              ? (event) => {
                  // A pointer click leaves no focus on the row: a
                  // later key press would otherwise turn it into a
                  // keyboard focus ring (Ben, 2026-09-28).
                  if (event.detail > 0) event.currentTarget.blur();
                  onRowClick(row, globalIndex, rowKey);
                }
              : undefined
          }
          onMouseLeave={onRowLeave}
          onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}
          onKeyDown={
            clickable
              ? (event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRowClick(row, globalIndex, rowKey);
                  }
                }
              : undefined
          }
          tabIndex={clickable ? 0 : undefined}
          aria-expanded={renderExpanded ? expanded : undefined}
          {...rowData}
          sx={[
            {
              backgroundColor: rowBackgroundColor,
              cursor: clickable ? "pointer" : undefined,
              "&:hover": {
                backgroundColor: colors.background.tertiary,
              },
              // The ring is drawn on the cells: a row's own outline
              // hides under a pinned first cell.
              "&:focus-visible": { outline: "none" },
              "&:focus-visible > td": {
                boxShadow: `inset 0 2px 0 ${colors.functional.success}, inset 0 -2px 0 ${colors.functional.success}`,
              },
              "&:focus-visible > td:first-of-type": {
                boxShadow: `inset 2px 0 0 ${colors.functional.success}, inset 0 2px 0 ${colors.functional.success}, inset 0 -2px 0 ${colors.functional.success}`,
              },
              "&:focus-visible > td:last-of-type": {
                boxShadow: `inset -2px 0 0 ${colors.functional.success}, inset 0 2px 0 ${colors.functional.success}, inset 0 -2px 0 ${colors.functional.success}`,
              },
              "&:last-child td": {
                borderBottom: "none",
              },
            },
            ...(Array.isArray(rowSx) ? rowSx : [rowSx ?? false]),
          ]}
        >
          {columns.map((column, columnIndex) => {
            const value = getNestedValue(row, String(column.key));

            return (
              <TableCell
                key={String(column.key)}
                align={column.align ?? "left"}
                sx={cellSx(column, columnIndex, expanded)}
              >
                {column.render ? (
                  column.render(value, row, globalIndex)
                ) : (
                  <Typography
                    sx={{
                      ...portfolioTypography.body,
                      color: colors.text.primary,
                    }}
                  >
                    {value ?? "-"}
                  </Typography>
                )}
              </TableCell>
            );
          })}
        </TableRow>
        {renderExpanded && (expanded || animateExpanded) && (
          <TableRow
            aria-hidden={!expanded}
            sx={{ backgroundColor: rowBackgroundColor }}
          >
            <TableCell
              colSpan={columns.length}
              sx={{
                p: 0,
                borderBottom: expanded
                  ? `1px solid ${colors.border.secondary}`
                  : 0,
              }}
            >
              <Collapse
                in={expanded}
                timeout={animateExpanded && !reducedMotion ? 180 : 0}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={
                    stickyFirstColumn
                      ? {
                          position: "sticky",
                          left: 0,
                          width: "var(--visible-width, 100cqw)",
                        }
                      : undefined
                  }
                >
                  {renderExpanded(row, globalIndex)}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ backgroundColor: rowBackgroundColor }}>
      {/* Table */}
      <TableContainer
        ref={scrollBoxRef}
        sx={{
          maxHeight: maxHeight,
          overflowX: stickyFirstColumn ? "auto" : undefined,
          // The expanded row's content sizes to this box (100cqw), not the page.
          containerType: spansVisibleWidth ? "inline-size" : undefined,
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: colors.background.secondary,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.border.primary,
            borderRadius: 4,
          },
        }}
      >
        <Table
          stickyHeader={stickyHeader}
          size="small"
          aria-busy={loadingRows !== undefined || undefined}
          sx={{ borderCollapse: "collapse", borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: colors.background.secondary,
              }}
            >
              {columns.map((column, columnIndex) => {
                const isSortable = sortable && column.sortable !== false;
                const pinned = stickyFirstColumn && columnIndex === 0;

                return (
                  <SortableColumnHeader
                    key={String(column.key)}
                    label={column.header}
                    sortKey={String(column.key)}
                    currentSortDirection={getSortDirection(column.key)}
                    onSort={requestSort}
                    sortable={isSortable}
                    align={column.align}
                    sx={{
                      width: column.width,
                      backgroundColor: colors.background.secondary,
                      ...(pinned
                        ? { position: "sticky", left: 0, zIndex: 3 }
                        : {}),
                    }}
                  />
                );
              })}
            </TableRow>
          </TableHead>
          {pinned && pinnedRows.length > 0 && (
            <TableBody data-pinned aria-labelledby={pinned.labelledBy}>
              {sectionRow(pinned.header)}
              {pinnedRows.map((row, rowIndex) =>
                renderRow(
                  row,
                  rowIndex,
                  rowIndex,
                  pinned.rowSx,
                  pinned.rowData?.(row),
                ),
              )}
              {pinned.footer !== undefined && sectionRow(pinned.footer)}
            </TableBody>
          )}
          {pinned &&
            pinnedRows.length > 0 &&
            displayData.length === 0 &&
            (pinned.loadingRows !== undefined ||
              pinned.panel !== undefined) && (
              <TableBody>
                {pinned.loadingRows !== undefined &&
                  pinned.bodyHeader !== undefined &&
                  sectionRow(pinned.bodyHeader)}
                {pinned.loadingRows !== undefined &&
                  skeletonRows(pinned.loadingRows)}
                {pinned.panel !== undefined && sectionRow(pinned.panel)}
              </TableBody>
            )}
          {displayData.length > 0 && (
            <TableBody>
              {pinnedRows.length > 0 &&
                pinned?.bodyHeader !== undefined &&
                sectionRow(pinned.bodyHeader)}
              {displayData.map((row, rowIndex) =>
                renderRow(
                  row,
                  rowIndex,
                  serverMode ? rowIndex : startIndex + rowIndex,
                ),
              )}
            </TableBody>
          )}
          {loadingRows !== undefined && (
            <TableBody>{skeletonRows(loadingRows)}</TableBody>
          )}
        </Table>
        {footer && (
          <Box
            sx={
              stickyFirstColumn && renderExpanded
                ? { position: "sticky", left: 0 }
                : undefined
            }
          >
            {footer}
          </Box>
        )}
      </TableContainer>

      {/* Pagination */}
      {paginate && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={goToNextPage}
          onPreviousPage={goToPreviousPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </Box>
  );
};

export default DataTable;
